import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { RxCross2 } from "react-icons/rx";
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

const MEDIAPIPE_BASE = `${process.env.PUBLIC_URL || ''}/mediapipe`;

const SelfieCapture = ({ 
  isOpen, 
  onClose, 
  onCapture, 
  title = "Take Selfie", 
  buttonText = "Capture",
  showLoading = false,
  loadingText = "Processing...",
  extraContent = null,
  onBeforeCapture
}) => {
  const [cameraError, setCameraError] = useState('');
  const [hasCameraStream, setHasCameraStream] = useState(false);
  const [isDetectorReady, setIsDetectorReady] = useState(false);
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [isFaceInsideCircle, setIsFaceInsideCircle] = useState(false);
  const webcamRef = useRef(null);
  const overlayCircleRef = useRef(null);
  const faceDetectorRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  const videoConstraints = {
    width: { ideal: 640 },
    height: { ideal: 480 },
    facingMode: 'user'
  };

  const clearDetectionInterval = useCallback(() => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
  }, []);

  const resetFaceValidation = useCallback(() => {
    setIsFaceDetected(false);
    setIsFaceInsideCircle(false);
  }, []);

  const isFaceWithinGuideCircle = useCallback((detection, videoElement, circleElement) => {
    if (!detection?.boundingBox || !videoElement || !circleElement) {
      return false;
    }

    const boundingBox = detection.boundingBox;
    const videoRect = videoElement.getBoundingClientRect();
    const circleRect = circleElement.getBoundingClientRect();

    const displayWidth = videoRect.width;
    const displayHeight = videoRect.height;
    if (!displayWidth || !displayHeight) {
      return false;
    }

    const sourceWidth = videoElement.videoWidth || displayWidth;
    const sourceHeight = videoElement.videoHeight || displayHeight;
    const scaleX = displayWidth / sourceWidth;
    const scaleY = displayHeight / sourceHeight;

    const faceCenterX = (boundingBox.originX + (boundingBox.width / 2)) * scaleX;
    const faceCenterY = (boundingBox.originY + (boundingBox.height / 2)) * scaleY;
    const faceRadius = (Math.max(boundingBox.width * scaleX, boundingBox.height * scaleY) / 2);

    const circleCenterX = (circleRect.left - videoRect.left) + (circleRect.width / 2);
    const circleCenterY = (circleRect.top - videoRect.top) + (circleRect.height / 2);
    const circleRadius = Math.min(circleRect.width, circleRect.height) / 2;

    const dx = faceCenterX - circleCenterX;
    const dy = faceCenterY - circleCenterY;
    const centerDistance = Math.sqrt((dx * dx) + (dy * dy));

    // Keep a strict fit so most of the face stays inside the guide.
    return centerDistance + (faceRadius * 0.8) <= circleRadius;
  }, []);

  const runFaceDetection = useCallback(() => {
    if (!faceDetectorRef.current || !webcamRef.current?.video || !overlayCircleRef.current) {
      resetFaceValidation();
      return;
    }

    const videoElement = webcamRef.current.video;
    if (
      videoElement.readyState < 2
      || !videoElement.videoWidth
      || !videoElement.videoHeight
    ) {
      resetFaceValidation();
      return;
    }

    try {
      const detectionResult = faceDetectorRef.current.detectForVideo(videoElement, performance.now());
      const firstDetection = detectionResult?.detections?.[0];
      const hasFace = Boolean(firstDetection);
      setIsFaceDetected(hasFace);
      setIsFaceInsideCircle(hasFace && isFaceWithinGuideCircle(firstDetection, videoElement, overlayCircleRef.current));
    } catch (error) {
      console.error('Face detection failed:', error);
      resetFaceValidation();
    }
  }, [isFaceWithinGuideCircle, resetFaceValidation]);

  useEffect(() => {
    let disposed = false;

    const initDetector = async () => {
      if (!isOpen) {
        return;
      }
      try {
        const fileset = await FilesetResolver.forVisionTasks(`${MEDIAPIPE_BASE}/wasm`);
        if (disposed) {
          return;
        }
        faceDetectorRef.current = await FaceDetector.createFromOptions(fileset, {
          baseOptions: {
            modelAssetPath: `${MEDIAPIPE_BASE}/models/blaze_face_short_range.tflite`
          },
          runningMode: 'VIDEO',
          minDetectionConfidence: 0.5
        });
        if (!disposed) {
          setIsDetectorReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize face detector:', error);
        if (!disposed) {
          setCameraError('Face detection failed to initialize. Please refresh and try again.');
          setIsDetectorReady(false);
        }
      }
    };

    if (isOpen) {
      setCameraError('');
      setHasCameraStream(false);
      setIsDetectorReady(false);
      resetFaceValidation();
      initDetector();
    }

    return () => {
      disposed = true;
      clearDetectionInterval();
      faceDetectorRef.current?.close?.();
      faceDetectorRef.current = null;
      setIsDetectorReady(false);
      setHasCameraStream(false);
      resetFaceValidation();
    };
  }, [isOpen, clearDetectionInterval, resetFaceValidation]);

  useEffect(() => {
    const shouldRunDetection = isOpen && !cameraError && !showLoading && hasCameraStream && isDetectorReady;
    if (!shouldRunDetection) {
      clearDetectionInterval();
      return;
    }

    runFaceDetection();
    detectionIntervalRef.current = setInterval(runFaceDetection, 250);

    return () => {
      clearDetectionInterval();
    };
  }, [
    isOpen,
    cameraError,
    showLoading,
    hasCameraStream,
    isDetectorReady,
    runFaceDetection,
    clearDetectionInterval
  ]);

  const capture = useCallback(() => {
    if (!isFaceInsideCircle) {
      return;
    }

    if (typeof onBeforeCapture === 'function') {
      const ok = onBeforeCapture();
      if (ok === false) {
        return;
      }
    }
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc && onCapture) {
      onCapture(imageSrc);
    }
  }, [webcamRef, onCapture, isFaceInsideCircle, onBeforeCapture]);

  const handleClose = () => {
    setCameraError('');
    clearDetectionInterval();
    faceDetectorRef.current?.close?.();
    faceDetectorRef.current = null;
    setIsDetectorReady(false);
    setHasCameraStream(false);
    resetFaceValidation();
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-50 p-3 sm:p-4 overflow-y-auto overscroll-contain sm:items-center">
      <div className="bg-white rounded-lg w-full max-w-2xl min-w-0 relative flex flex-col max-h-[min(90vh,calc(100dvh-2rem))] my-2 sm:my-4 mx-auto overflow-hidden">

        <RxCross2
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 cursor-pointer text-xl z-10"
        />
        
        <div className="text-center p-6 pb-4">
          <h3 className="text-lg font-semibold text-[#674936]">{title}</h3>
          <p className="text-sm text-gray-600">Position your face in the center and click check in</p>
        </div>
        
        <div className="overflow-y-auto flex-1 px-6">
          {cameraError ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{cameraError}</p>
              <button
                onClick={handleClose}
                className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#503220] transition-colors"
              >
                Close
              </button>
            </div>
          ) : showLoading ? (
            <div className="text-center py-8">
              <div className="mb-4">
                <div className="w-12 h-12 border-4 border-t-[#674936] border-gray-300 rounded-full animate-spin mx-auto"></div>
              </div>
              <h3 className="text-lg font-semibold text-[#674936] mb-2">Processing</h3>
              <p className="text-sm text-gray-600">{loadingText}</p>
            </div>
          ) : (
            <div className="text-center">
              <div className="relative mb-4 bg-gray-100 rounded-lg overflow-hidden w-full">
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  width={640}
                  height={480}
                  mirrored={true}
                  className="w-full h-auto max-h-80 object-cover"
                  onUserMedia={(stream) => {
                    setCameraError('');
                    setHasCameraStream(Boolean(stream));
                  }}
                  onUserMediaError={(error) => {
                    console.error('Camera error:', error);
                    setHasCameraStream(false);
                    resetFaceValidation();
                    setCameraError('Unable to access camera. Please ensure camera permissions are granted.');
                  }}
                />
                
                <div className="absolute inset-0 pointer-events-none">
                  <div className="flex items-center justify-center h-full">
                    <div
                      ref={overlayCircleRef}
                      className={`border-2 rounded-full w-48 h-48 opacity-80 transition-colors duration-200 ${
                        isFaceInsideCircle ? 'border-green-500' : 'border-white'
                      }`}
                    >
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm mb-3 text-gray-600">
                {isFaceInsideCircle
                  ? 'Face detected inside circle. You can continue.'
                  : isFaceDetected
                    ? 'Move your face to the center circle to continue.'
                    : 'No face detected. Align your face inside the circle.'}
              </p>
              {extraContent}
            </div>
          )}
        </div>
        
        {!cameraError && !showLoading && (
          <div className="flex gap-4 justify-center p-6 pt-4 border-t">
            <button
              onClick={capture}
              disabled={!isFaceInsideCircle || !isDetectorReady}
              className={`text-white px-6 py-3 rounded-lg transition-colors flex items-center gap-2 font-semibold ${
                isFaceInsideCircle && isDetectorReady
                  ? 'bg-[#674936] hover:bg-[#503220]'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
               {buttonText}
            </button>
            <button
              onClick={handleClose}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfieCapture;
